/**
 * AFK assist timer. Fires `onTimeout` after `timeoutMs` of inactivity, but
 * only when the consumer-supplied `isLocalTurn` predicate returns true at fire
 * time. This guards against firing while the opponent is moving / animations
 * are in progress / the player isn't actually expected to act.
 *
 * Public API:
 *   - `arm()` — start (or restart) the countdown.
 *   - `disarm()` — cancel the pending timer; safe to call when not armed.
 *   - `poke()` — reset the countdown if armed; no-op when disarmed.
 *   - `setTimeoutMs(ms)` — adjust the timeout; takes effect on next `arm()`.
 *
 * The "pick a legal move and pulse-highlight it" hint logic is per-app and
 * lives in each game's overlay layer — this timer only owns activity tracking
 * and the chime trigger.
 */
export interface AfkAssistHandlers {
  isLocalTurn(): boolean;
  onTimeout(): void;
}

export class AfkTimer {
  private readonly handlers: AfkAssistHandlers;
  private timeoutMs: number;
  private timerId: ReturnType<typeof setTimeout> | null = null;

  constructor(handlers: AfkAssistHandlers, timeoutMs: number) {
    this.handlers = handlers;
    this.timeoutMs = timeoutMs;
  }

  arm(): void {
    this.disarm();
    this.timerId = setTimeout(() => {
      this.timerId = null;
      if (this.handlers.isLocalTurn()) {
        this.handlers.onTimeout();
      }
    }, this.timeoutMs);
  }

  disarm(): void {
    if (this.timerId !== null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  poke(): void {
    if (this.timerId === null) return;
    this.arm();
  }

  setTimeoutMs(ms: number): void {
    this.timeoutMs = ms;
  }
}
